import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
  const eventProductId = event.detail.data.newProduct?.id || event.detail.data.productId;

  if (event.detail.data.newProduct) {
    this.dataset.productId = event.detail.data.newProduct.id;
  } else if (eventProductId && eventProductId !== this.dataset.productId) {
    return;
  }

  const currentPriceContainer = this.querySelector('[ref="priceContainer"]');
  const currentVolumePricingNote = this.querySelector('[ref="volumePricingNote"]');

  const newProductPrice = event.detail.data.html.querySelector(
    `product-price[data-block-id="${this.dataset.blockId}"]`
  );

  if (!newProductPrice) return;

  const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');

  if (newPrice && currentPriceContainer) {
    currentPriceContainer.replaceWith(newPrice.cloneNode(true));
  }

  const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
  const latestPriceContainer = this.querySelector('[ref="priceContainer"]');

  if (!newNote) {
    currentVolumePricingNote?.remove();
  } else if (!currentVolumePricingNote) {
    latestPriceContainer?.insertAdjacentElement('afterend', newNote.cloneNode(true));
  } else {
    currentVolumePricingNote.replaceWith(newNote.cloneNode(true));
  }

  const inputSelector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
  const installmentsInput = this.querySelector(inputSelector);

  if (installmentsInput) {
    installmentsInput.value = event.detail.resource?.id ?? '';
    installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
};
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
